/**
 * R-167 — per-JobType display-template section.
 *
 * Mounted on the JobTypeDetailPage General tab. Renders three template
 * slots (batch / document / page) with:
 *   - Inherit-from-tenant toggle. When ON the local value is null and
 *     the (disabled) input shows the tenant-level fallback in muted text.
 *     When OFF the value is owned by this job type.
 *   - Live preview computed via `renderDisplayTemplate(template, sample)`.
 *   - Validator chips driven by `validateTemplate(template, ctx)` — flags
 *     unknown tokens, malformed counter/field tokens, and references to
 *     counters/fields that don't exist for this job type or tenant.
 *   - Shared `TokenCatalogPanel` cheatsheet — clicking a token inserts
 *     it into whichever template input was last focused.
 */
import { useMemo, useState } from "react";
import {
  renderDisplayTemplate,
  validateTemplate,
  type TemplateValidationIssue,
} from "@capture/shared";
import TokenCatalogPanel from "./TokenCatalogPanel";

export interface JobTypeShape {
  id: string;
  name: string;
  shortName: string;
  batchDisplayTemplate: string | null;
  documentDisplayTemplate: string | null;
  pageDisplayTemplate: string | null;
  // R-167 WU-8 — which source drives the canonical doc-number column in this
  // job type's exports. `null` defers to the historical `_displayId` behavior;
  // `"displayId"` is an explicit no-op; `"customDisplayLabel"` auto-binds the
  // first identifier mapping to `_customDisplayLabel` in the export pipeline.
  defaultDocNumberSource: string | null;
}

export interface TenantFallback {
  batchDisplayTemplate: string | null;
  documentDisplayTemplate: string | null;
  pageDisplayTemplate: string | null;
}

export type TemplatePatch = Partial<
  Pick<
    JobTypeShape,
    | "batchDisplayTemplate"
    | "documentDisplayTemplate"
    | "pageDisplayTemplate"
    | "defaultDocNumberSource"
  >
>;

export interface JobTypeDisplayTemplatesSectionProps {
  jobType: JobTypeShape;
  tenantFallback: TenantFallback;
  knownCounterNames: Set<string>;
  knownFieldInternalNames: Set<string>;
  onSave: (patch: TemplatePatch) => void;
}

type SlotKey = "batchDisplayTemplate" | "documentDisplayTemplate" | "pageDisplayTemplate";

const SLOTS: Array<{ key: SlotKey; label: string }> = [
  { key: "batchDisplayTemplate", label: "Batch label template" },
  { key: "documentDisplayTemplate", label: "Document label template" },
  { key: "pageDisplayTemplate", label: "Page label template" },
];

// Sample record drives the live preview. The non-identity fields are
// hard-coded so admins can see the effect without real batch/document data,
// but `jobType.shortName` is injected from the ACTUAL job type at render time
// (R-181) so the preview never misleads the operator into thinking their JT
// has a short name configured when it doesn't.
const SAMPLE_RECORD = {
  displayId: "NOW_20260527_0001",
  createdAt: new Date(Date.UTC(2026, 4, 27, 0, 0, 0)),
  counterValues: { batchSeq: 2003 },
  counterPadding: { batchSeq: 4 },
  sourceCode: "SCN",
  fieldValues: { claim_number: "ABC-99" },
  docPosition: 3,
  pagePosition: 17,
};

// Placeholder rendered in the preview's `{shortName}` slot when the job type
// has no short name set — gives the operator an immediate visual cue (R-181).
const NO_SHORT_NAME_PLACEHOLDER = "(no short name set)";

// Matches a `{shortName}` token anywhere in a template string (R-181 warning).
const SHORT_NAME_TOKEN_RE = /\{shortName\}/;

const sectionStyle: React.CSSProperties = {
  padding: "16px 0",
  borderTop: "1px solid var(--color-border)",
  marginTop: 16,
};

const headerStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 8,
};

const helpStyle: React.CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-secondary)",
  marginBottom: 12,
};

const slotWrapStyle: React.CSSProperties = {
  marginBottom: 16,
};

const slotLabelRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: 4,
  flexWrap: "wrap",
};

const slotLabelStyle: React.CSSProperties = {
  fontWeight: 600,
  fontSize: 14,
};

const inheritToggleStyle: React.CSSProperties = {
  fontSize: 12,
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  color: "var(--color-text-secondary)",
};

const previewStyle: React.CSSProperties = {
  fontSize: 12,
  marginTop: 4,
  color: "var(--color-text-secondary)",
};

const issueListStyle: React.CSSProperties = {
  fontSize: 12,
  color: "#dc2626",
  margin: "4px 0 0",
  padding: 0,
  listStyle: "none",
};

const issueItemStyle: React.CSSProperties = {
  display: "inline-block",
  background: "rgba(220, 38, 38, 0.1)",
  border: "1px solid #dc2626",
  borderRadius: 4,
  padding: "2px 6px",
  marginRight: 4,
  marginTop: 4,
};

const shortNameWarningStyle: React.CSSProperties = {
  fontSize: 13,
  color: "#92400e",
  background: "rgba(255, 193, 7, 0.12)",
  border: "1px solid #FFC107",
  borderRadius: 4,
  padding: "8px 10px",
  margin: "0 0 12px",
};

const tokensBtnStyle: React.CSSProperties = {
  padding: "4px 10px",
  background: "transparent",
  color: "#ea580c",
  border: "1px solid #ea580c",
  borderRadius: 4,
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
};

function inputStyle(inheriting: boolean): React.CSSProperties {
  return {
    width: "100%",
    padding: 8,
    fontFamily: "var(--font-mono, monospace)",
    fontSize: 13,
    background: inheriting ? "var(--color-bg-tertiary)" : "var(--color-bg-secondary)",
    color: inheriting ? "var(--color-text-muted)" : "var(--color-text-primary)",
    border: "1px solid var(--color-border)",
    borderRadius: 4,
    boxSizing: "border-box",
  };
}

export default function JobTypeDisplayTemplatesSection({
  jobType,
  tenantFallback,
  knownCounterNames,
  knownFieldInternalNames,
  onSave,
}: JobTypeDisplayTemplatesSectionProps) {
  const [values, setValues] = useState<Record<SlotKey, string | null>>({
    batchDisplayTemplate: jobType.batchDisplayTemplate,
    documentDisplayTemplate: jobType.documentDisplayTemplate,
    pageDisplayTemplate: jobType.pageDisplayTemplate,
  });
  // R-167 WU-8 Task 8.5 — tracks the dropdown choice locally so the section is
  // a single self-contained patch source. `null` and `"displayId"` are
  // visually distinct in the dropdown (one labelled "(inherit / default)",
  // the other "Display ID") but functionally identical downstream — the
  // export pipeline only swaps when the value is exactly "customDisplayLabel".
  const [docNumberSource, setDocNumberSource] = useState<string | null>(
    jobType.defaultDocNumberSource,
  );
  const [focused, setFocused] = useState<SlotKey | null>(null);
  const [cheatsheetOpen, setCheatsheetOpen] = useState(false);

  function setDocSource(next: string | null) {
    setDocNumberSource(next);
    onSave({ defaultDocNumberSource: next });
  }

  const ctx = useMemo(
    () => ({ knownCounterNames, knownFieldInternalNames }),
    [knownCounterNames, knownFieldInternalNames],
  );

  // R-181 — inject the REAL job type's short name into the preview sample so
  // the rendered label matches what an actual batch would get. When the JT
  // has no short name, fall back to a visible placeholder rather than the old
  // hard-coded "DE1545" (which falsely implied a short name was configured).
  const shortNameSet = !!(jobType.shortName && jobType.shortName.trim());
  const sampleRecord = useMemo(
    () => ({
      ...SAMPLE_RECORD,
      jobType: {
        shortName: shortNameSet ? jobType.shortName : NO_SHORT_NAME_PLACEHOLDER,
      },
    }),
    [shortNameSet, jobType.shortName],
  );

  function setSlot(key: SlotKey, value: string | null) {
    setValues((prev) => ({ ...prev, [key]: value }));
    onSave({ [key]: value } as TemplatePatch);
  }

  function insertToken(token: string) {
    if (!focused) return;
    const current = values[focused] ?? "";
    setSlot(focused, current + token);
  }

  // R-181 — true when any effective (own or inherited) template references the
  // `{shortName}` token. Combined with `!shortNameSet` this drives the warning
  // banner telling the operator the token will render empty on real records.
  const anyTemplateUsesShortName = SLOTS.some((slot) => {
    const localValue = values[slot.key];
    const effective = localValue === null ? tenantFallback[slot.key] : localValue;
    return !!effective && SHORT_NAME_TOKEN_RE.test(effective);
  });
  const showShortNameWarning = anyTemplateUsesShortName && !shortNameSet;

  return (
    <section style={sectionStyle} data-testid="job-type-display-templates-section">
      <header style={headerStyle}>
        <h3 style={{ margin: 0, fontSize: 16, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Display Templates
        </h3>
        <button
          type="button"
          onClick={() => setCheatsheetOpen(true)}
          style={tokensBtnStyle}
          data-testid="job-type-tokens-button"
        >
          Tokens…
        </button>
      </header>
      <p style={helpStyle}>
        Override the tenant-level templates for this job type. Toggle{" "}
        <strong>Inherit from tenant</strong> off to set a custom template. The
        underlying display id is unchanged — only the operator-visible label
        is reformatted.
      </p>

      {/* R-183 — counter inheritance rule. The preview already reflects this:
          the same {counter:NAME} on the batch + document + page slots renders
          the SAME value because the doc/page inherit the batch's allocation
          (see resolveCountersWithInheritance in custom-display-label.ts). */}
      <p style={helpStyle} data-testid="job-type-counter-inheritance-help">
        Counter tokens (<code>{`{counter:NAME}`}</code>) on document or page
        templates <strong>inherit the parent batch's value</strong> when the
        same name is referenced — the counter advances once per batch, and the
        doc/page reuse that number. To get a fresh allocation per document or
        page, use a different counter name (e.g. <code>docSeq</code> on the
        document template, <code>pageSeq</code> on the page template).
      </p>

      {showShortNameWarning && (
        <div
          role="alert"
          style={shortNameWarningStyle}
          data-testid="job-type-shortname-warning"
        >
          This job type has no <strong>Short Name</strong> set, so the{" "}
          <code>{`{shortName}`}</code> token will render empty on real records.
          Set a short name on the General tab above to populate it.
        </div>
      )}

      {SLOTS.map((slot) => {
        const localValue = values[slot.key];
        const inheriting = localValue === null;
        const fallback = tenantFallback[slot.key];
        const effective = inheriting ? fallback : localValue;
        const issues: TemplateValidationIssue[] = effective
          ? validateTemplate(effective, ctx)
          : [];
        const preview = effective
          ? renderDisplayTemplate(effective, sampleRecord)
          : "(no template — falls back to displayId)";

        return (
          <div key={slot.key} style={slotWrapStyle}>
            <div style={slotLabelRowStyle}>
              <span style={slotLabelStyle}>{slot.label}</span>
              <label style={inheritToggleStyle}>
                <input
                  type="checkbox"
                  checked={inheriting}
                  onChange={(e) =>
                    setSlot(slot.key, e.target.checked ? null : fallback ?? "")
                  }
                  aria-label={`inherit ${slot.label}`}
                />
                <span>Inherit from tenant</span>
              </label>
            </div>
            <input
              type="text"
              value={effective ?? ""}
              disabled={inheriting}
              onChange={(e) => setSlot(slot.key, e.target.value)}
              onFocus={() => setFocused(slot.key)}
              style={inputStyle(inheriting)}
              aria-label={slot.label}
              data-testid={`job-type-${slot.key}-input`}
            />
            <div style={previewStyle} data-testid={`job-type-${slot.key}-preview`}>
              Preview: <code>{preview}</code>
            </div>
            {issues.length > 0 && (
              <ul style={issueListStyle} data-testid={`job-type-${slot.key}-issues`}>
                {issues.map((i, idx) => (
                  <li key={idx} style={issueItemStyle}>
                    <strong>{i.kind}</strong>: {i.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}

      {/* R-167 WU-8 Task 8.5 — opt-in flag: which source drives the canonical
          doc-number column in this job type's exports. The export pipeline
          inspects this value when applying field-mapping resolution. */}
      <div style={{ ...slotWrapStyle, marginTop: 20 }}>
        <div style={slotLabelRowStyle}>
          <span style={slotLabelStyle}>Default document number source</span>
        </div>
        <select
          value={docNumberSource ?? ""}
          onChange={(e) => setDocSource(e.target.value === "" ? null : e.target.value)}
          aria-label="Default document number source"
          data-testid="job-type-default-doc-number-source"
          style={{
            ...inputStyle(false),
            fontFamily: "inherit",
          }}
        >
          <option value="">
            Built-in Display ID — e.g. NOW_20260527_0001 (default)
          </option>
          <option value="displayId">
            Built-in Display ID — e.g. NOW_20260527_0001
          </option>
          <option value="customDisplayLabel">
            Your custom label from the templates above — e.g.
            20260527-DE1545-2000-SCN
          </option>
        </select>
        <div style={{ ...helpStyle, marginTop: 6, marginBottom: 0 }}>
          Every batch and document in Capture has two identifiers: a{" "}
          <strong>built-in Display ID</strong> (e.g.{" "}
          <code>NOW_20260527_0001</code>) and{" "}
          <strong>your custom label</strong> rendered from the templates above
          (e.g. <code>20260527-DE1545-2000-SCN</code>). This setting controls
          which one ships as the document number in export files. Choose{" "}
          <strong>your custom label</strong> to export your tenant-meaningful
          label; leave on the default to export the built-in Display ID. (When
          a record has no custom label stamped, exports fall back to the
          Display ID.)
        </div>
      </div>

      {cheatsheetOpen && (
        <TokenCatalogPanel
          onInsert={insertToken}
          onClose={() => setCheatsheetOpen(false)}
        />
      )}
    </section>
  );
}
